import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardPlus, Stethoscope } from 'lucide-react';
import mindProfessionalImage from '@/assets/images/session/mind-professional.svg';
import doctorProfessionalImage from '@/assets/images/session/doctor-professional.svg';
import {
	decodeUrl,
	encodeUrl,
	scrollSchedulingWizardToTop,
} from '@/utils';
import { sortByMatchRank, useMatchedProfessionalRanks } from '@/hooks';

import {
	useGetProfessionalPublic,
	useGetAllProfessionalsPublic,
	useGetServices,
} from '@/services';

import {
	SearchInput,
	ProfessionalInfoCard,
	SessionStepTitle,
	ProfessionalCard,
	StepButtonContainer,
} from '@/components';
import { E2E_SCHEDULING } from '@/constants/e2eSchedulingTestIds';

/** Largura uniforme dos cards (serviço e profissional) no grid de agendamento. */
/** Profissionais em lista (um card horizontal por linha). */
const PROFESSIONAL_LIST_CLASS = 'flex w-full flex-col gap-4';

const SCHEDULING_CARD_GRID_CLASS =
	'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4';

interface StepProfessionalListProps {
	showBackButton?: boolean;
}

const StepProfessionalList = ({
	showBackButton,
}: StepProfessionalListProps) => {
	const professionalsContainerRef = useRef<HTMLDivElement>(null);
	const [searchParams] = useSearchParams();
	/** Profissionais do match da conta logada (vêm primeiro e ganham o selo). */
	const matchRanks = useMatchedProfessionalRanks();
	const [selectedService, setSelectedService] =
		useState<Services.Service | null>(null);
	const [selectedProfessionalId, setSelectedProfessionalId] = useState<
		string | null
	>(null);
	const navigate = useNavigate();

	const search = searchParams.get('search')?.trim();
	const isNameSearchMode =
		searchParams.get('name_search') === '1' && Boolean(search);
	const serviceHint = searchParams.get('service')?.toLowerCase();
	const preferredServiceId = searchParams.get('service_id');
	const isServicePreselectedFromUrl =
		Boolean(preferredServiceId) || Boolean(serviceHint);
	const hideServicePicker = isNameSearchMode || isServicePreselectedFromUrl;
	const encodedParams = searchParams.get('d');

	let decodedParams: { p: string; s: string } | undefined;

	if (encodedParams) {
		decodedParams = decodeUrl<{ s: string; p: string }>(encodedParams);
	}

	useEffect(() => {
		if (decodedParams?.p) {
			setSelectedProfessionalId(decodedParams.p);
		}
	}, [decodedParams?.p]);

	const { data: professionalDefaultData } = useGetProfessionalPublic(
		decodedParams?.p || '',
		{
			enabled: !!decodedParams?.p,
		},
	);

	const {
		data: professionalsData,
		isFetching,
		isError,
	} = useGetAllProfessionalsPublic(
		{
			...(!isNameSearchMode &&
				(selectedService?.id ?? preferredServiceId) && {
					service_id: selectedService?.id ?? preferredServiceId!,
				}),
			...(search && { search }),
		},
		{
			enabled:
				isNameSearchMode ||
				Boolean(selectedService?.id ?? preferredServiceId),
		},
	);

	const { data: serviceData } = useGetServices({
		filters: { active: true, type: 'PROFESSION' },
	});

	useEffect(() => {
		if (isNameSearchMode || !serviceData?.length || selectedService) return;

		if (preferredServiceId) {
			const byId = serviceData.find(
				(service) => service.id === preferredServiceId,
			);
			if (byId) {
				setSelectedService(byId);
				return;
			}
		}

		if (serviceHint) {
			const normalize = (value: string) => value.toLowerCase();
			const matchedService = serviceData.find((service) => {
				const serviceName = normalize(service.service_name || '');
				const labelName = normalize(service.professional_label || '');
				const haystack = `${serviceName} ${labelName}`;

				if (serviceHint === 'psychologist')
					return haystack.includes('psicolog');
				if (serviceHint === 'psychiatrist')
					return (
						haystack.includes('psiquiat') ||
						haystack.includes('médic')
					);

				return false;
			});

			if (matchedService) {
				setSelectedService(matchedService);
				return;
			}
		}

		if (serviceData.length === 1) {
			setSelectedService(serviceData[0]!);
		}
	}, [
		serviceData,
		preferredServiceId,
		serviceHint,
		selectedService,
		isNameSearchMode,
	]);

	useEffect(() => {
		if (professionalDefaultData)
			setSelectedProfessionalId(professionalDefaultData.id);
	}, [professionalDefaultData]);

	useEffect(() => {
		if (
			isNameSearchMode ||
			!professionalDefaultData ||
			!serviceData ||
			selectedService
		)
			return;

		const professionalServiceId =
			(professionalDefaultData as any)?.professional_services?.[0]
				?.service?.id ??
			(professionalDefaultData as any)?.professional_services?.[0]
				?.service_id ??
			null;

		if (!professionalServiceId) return;

		const matchingService = serviceData.find(
			(service) => service.id === professionalServiceId,
		);

		if (matchingService) {
			setSelectedService(matchingService);
		}
	}, [
		professionalDefaultData,
		serviceData,
		selectedService,
		isNameSearchMode,
	]);

	const professionalsList = useMemo(() => {
		const list =
			professionalsData?.data ?? professionalsData?.professionals ?? [];
		// Indicados no match vêm primeiro (1º, 2º, 3º); o resto mantém a ordem do admin.
		return sortByMatchRank(list, matchRanks);
	}, [professionalsData, matchRanks]);

	const scrollToProfessionalsSection = () => {
		professionalsContainerRef.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	};

	useEffect(() => {
		if (!professionalsData || isFetching) return;

		if (selectedProfessionalId) {
			const frameId = requestAnimationFrame(() => {
				scrollSchedulingWizardToTop();
			});
			return () => cancelAnimationFrame(frameId);
		}

		if (professionalsList.length > 0) {
			scrollToProfessionalsSection();
		}
	}, [
		professionalsData,
		professionalsList,
		selectedProfessionalId,
		isFetching,
	]);

	const handleProfessionalTypeSelected = (service: Services.Service) => {
		setSelectedService(service);
	};

	const handleProfessionalSelected = (
		id: string,
		slot?: { start_time: string },
	) => {
		scrollSchedulingWizardToTop();
		setSelectedProfessionalId(id);

		const newParams = new URLSearchParams(searchParams.toString());
		// `t` = horário escolhido no painel "Conheça o profissional"; o passo 2 abre com ele selecionado.
		newParams.set(
			'd',
			encodeUrl(
				slot ? { s: 2, p: id, t: slot.start_time } : { s: 2, p: id },
			),
		);
		navigate(`?${newParams.toString()}`, { replace: true });
	};

	const ServiceContainer = (service: Services.Service) => {
		const normalizedName = service.service_name.toLowerCase();
		const isDoctor =
			normalizedName.includes('médico') ||
			normalizedName.includes('psiquiatra');
		const icon = isDoctor ? doctorProfessionalImage : mindProfessionalImage;

		const isSelected = selectedService?.id === service.id;

		return (
			<ProfessionalInfoCard
				key={service.id}
				imageUrl={icon}
				title={service.service_name}
				dataTestId={`${E2E_SCHEDULING.SERVICE_CARD_PREFIX}${service.id}`}
				onClick={() => {
					handleProfessionalTypeSelected(service);
				}}
				description={service.short_description ?? undefined}
				isSelected={isSelected}
			/>
		);
	};

	const handlePrevius = () => navigate('/home');

	const showProfessionalsPanel =
		isNameSearchMode || !!selectedService || isServicePreselectedFromUrl;

	return (
		<section className="flex w-full flex-col items-center gap-6 pb-12 md:pb-16">
			{!hideServicePicker && (
				<>
					<SessionStepTitle
						title="Selecione o serviço que você busca:"
						icon={<ClipboardPlus className="w-5" />}
					/>

					<div className="h-full w-full">
						<div className={SCHEDULING_CARD_GRID_CLASS}>
							{serviceData &&
								serviceData.map((service) => (
									<ServiceContainer
										key={service.id}
										{...service}
									/>
								))}
						</div>
					</div>
				</>
			)}

			{showProfessionalsPanel && (
				<div
					ref={professionalsContainerRef}
					className="w-full space-y-4"
				>
					{!isNameSearchMode &&
						selectedService &&
						(() => {
							const description =
								selectedService.long_description?.trim() ||
								selectedService.description?.trim() ||
								selectedService.short_description?.trim() ||
								'';
							const serviceName =
								selectedService.service_name?.trim() ||
								selectedService.professional_label?.trim() ||
								'';

							if (!description) return null;

							return (
								<div className="w-full rounded-2xl border border-zinc-100 bg-white px-4 py-3 md:px-5 md:py-3.5">
									{serviceName ? (
										<h3 className="text-[12px] font-bold leading-tight text-zinc-900 md:text-[13px]">
											Sobre {serviceName}
										</h3>
									) : null}
									<p className="mt-1 text-[12px] font-medium leading-snug text-zinc-600 md:text-[13px]">
										{description}
									</p>
								</div>
							);
						})()}

					<SessionStepTitle
						className="w-fit"
						title="Selecione um profissional:"
						icon={<Stethoscope className="w-5" />}
					/>
					<SearchInput
						className="w-full md:w-96"
						placeholder="Pesquise um profissional ..."
					/>

					{professionalsList.length > 0 ? (
						<div className={PROFESSIONAL_LIST_CLASS}>
							{professionalsList.map((professional) => {
								const serviceName = (professional as any)
									.professional_services?.[0]?.service
									?.service_name;
								const isSelected =
									professional.id === selectedProfessionalId;

								return (
									<ProfessionalCard
										key={professional.id}
										professional_id={professional.id}
										name_prefix={
											(professional as any).name_prefix
										}
										first_name={professional.first_name}
										last_name={professional.last_name}
										profile_photo_url={
											professional.profile_photo_url ??
											undefined
										}
										video_url={professional.video_url}
										crm_or_crp={professional.crm_or_crp}
										brief_description={
											professional.brief_description
										}
										service_name={serviceName}
										highlight_tag={
											professional.highlight_tag
										}
										main_approach={
											professional.main_approach
										}
										secondary_approaches={
											professional.secondary_approaches
										}
										focus_topics={professional.focus_topics}
										matchRank={
											matchRanks[professional.id] ?? null
										}
										isSelected={isSelected}
										selectedButtonText="Continuar"
										selectButtonTestId={`${E2E_SCHEDULING.PROFESSIONAL_SELECT_PREFIX}${professional.id}`}
										onClick={() =>
											handleProfessionalSelected(
												professional.id,
											)
										}
										onScheduleSlot={(slot) =>
											handleProfessionalSelected(
												professional.id,
												slot,
											)
										}
									/>
								);
							})}
						</div>
					) : !isFetching && !isError ? (
						<div className="text-gray-500">
							Nenhum profissional encontrado
						</div>
					) : null}
				</div>
			)}
			<StepButtonContainer
				disabledNextButton={!selectedProfessionalId}
				onHandlePrevius={showBackButton ? handlePrevius : undefined}
			/>
		</section>
	);
};

export { StepProfessionalList };
